import { IProjectInfo } from 'src/generated-types/project'
import { IProjectStatus } from 'src/generated-types/project-status'
import { IProjectVersion } from 'src/generated-types/project-version'

export const MAX_VERSION_NAME_LENGTH = 150
export const MAX_VERSION_REASON_LENGTH = 1000

const isOpenDraftStatus = (status?: IProjectStatus) =>
  status === IProjectStatus.InProcess || status === IProjectStatus.OnHold

export const findOpenDraft = (versions?: IProjectVersion[]) => versions?.find(v => isOpenDraftStatus(v.status))

// A standalone project is never a draft, even while it is still in process.
export const isDraftVersion = (project: IProjectInfo) =>
  (project.version?.versionNumber ?? 1) > 1 && project.status !== IProjectStatus.Published

export const formatVersionLabel = (versionNumber: number) => `v${versionNumber}`

// Dialog title with the open revision appended, e.g. "Configure Positions · v2 · Replace anchors".
export const withDraftSuffix = (title: string, project: IProjectInfo) =>
  isDraftVersion(project)
    ? [title, formatVersionLabel(project.version?.versionNumber ?? 0), project.version?.name]
        .filter(Boolean)
        .join(' \u00b7 ')
    : title

// A published version stops being current once a newer one is published.
export const isSuperseded = (version: IProjectVersion, versions?: IProjectVersion[]) =>
  version.status === IProjectStatus.Published &&
  !!versions?.some(v => v.status === IProjectStatus.Published && v.versionNumber > version.versionNumber)

const SUPERSEDED_LABEL_KEY = 'Superseded'

// Translation key (projects namespace) for the status shown next to a version.
export const getVersionStatusKey = (version: IProjectVersion, versions?: IProjectVersion[]) =>
  isSuperseded(version, versions) ? SUPERSEDED_LABEL_KEY : IProjectStatus[version.status ?? IProjectStatus.Published]

export const findLatestPublished = (versions?: IProjectVersion[]) =>
  versions?.filter(v => v.status === IProjectStatus.Published).sort((a, b) => b.versionNumber - a.versionNumber)[0]

// Header chips: the current published version, the one in progress, and the selected one if it is neither.
export const getHeaderVersions = (versions: IProjectVersion[], selectedProjectId: number) => {
  const main = [findLatestPublished(versions), findOpenDraft(versions)].filter((v): v is IProjectVersion => !!v)
  const selected = versions.find(v => v.projectId === selectedProjectId)
  const extra = selected && !main.some(v => v.projectId === selected.projectId) ? selected : undefined
  return { main, extra }
}

export const filterVersions = (
  versions: IProjectVersion[],
  query: string,
  statusTextOf: (version: IProjectVersion) => string,
) => {
  const q = query.trim().toLowerCase()
  if (!q) return versions
  return versions.filter(v =>
    [
      formatVersionLabel(v.versionNumber),
      statusTextOf(v),
      v.createdByUserName,
      v.name,
      v.reason,
      new Date(v.createdAt).toLocaleDateString(),
    ]
      .join(' ')
      .toLowerCase()
      .includes(q),
  )
}

export interface IStatusChip {
  projectId: number
  status: IProjectStatus
  versionNumber?: number
}

// A versioned project is listed once; its chips are the latest published version and the one in progress.
export const getStatusChips = (project: IProjectInfo): IStatusChip[] => {
  const fallback: IStatusChip = {
    projectId: project.id,
    status: project.status,
    versionNumber: project.version?.versionNumber,
  }
  if (!project.versions?.length) return [fallback]

  const latestPublished = project.versions
    .filter(v => v.status === IProjectStatus.Published)
    .sort((a, b) => b.versionNumber - a.versionNumber)[0]
  const chips = [latestPublished, findOpenDraft(project.versions)]
    .filter((v): v is IProjectVersion => !!v)
    .map(v => ({ projectId: v.projectId, status: v.status as IProjectStatus, versionNumber: v.versionNumber }))
  return chips.length ? chips : [fallback]
}

// Sidebar highlighting: the single listed row stands for every version of the project.
export const containsProjectId = (project: IProjectInfo, projectId?: EntityId) =>
  String(project.id) === String(projectId) || !!project.versions?.some(v => String(v.projectId) === String(projectId))

// Only the newest version can be branched; a standalone project is its own latest.
export const isLatestVersion = (project: IProjectInfo, versions?: IProjectVersion[]) =>
  !project.version || (!!versions && versions.every(v => v.versionNumber <= (project.version?.versionNumber ?? 1)))
